import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import api from '../../../lib/api';

const cohortColors = {
  cohort1: '#0F766E',
  cohort2: '#EA580C',
  cohort3: '#7C3AED',
  cohort4: '#D97706',
};

const CohortAnalysis = () => {
  const [cohortData, setCohortData] = useState([]);
  const [cohortLabels, setCohortLabels] = useState({});
  const [activeCohorts, setActiveCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCohortData = async () => {
      try {
        setLoading(true);

        const { data: members } = await api.get('/members', { params: { limit: 2000 } });

        if (!members || members?.length === 0) {
          setLoading(false);
          return;
        }

        // Group members by join month (last 4 months)
        const now = new Date();
        const cohorts = {};
        const labels = {};

        for (let i = 3; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `cohort${4 - i}`;
          const monthLabel = d?.toLocaleString('default', { month: 'short' });
          cohorts[key] = { month: d?.getMonth(), year: d?.getFullYear(), members: [] };
          labels[key] = `${monthLabel} Cohort`;
        }

        members?.forEach(m => {
          if (!m?.join_date) return;
          const joinDate = new Date(m.join_date);
          Object.entries(cohorts)?.forEach(([key, cohort]) => {
            if (joinDate?.getMonth() === cohort?.month && joinDate?.getFullYear() === cohort?.year) {
              cohort?.members?.push(m);
            }
          });
        });

        // Build retention data points (M1 through M6)
        const chartData = [];
        for (let month = 1; month <= 6; month++) {
          const point = { month: `M${month}` };
          Object.entries(cohorts)?.forEach(([key, cohort]) => {
            if (cohort?.members?.length === 0) {
              point[key] = month === 1 ? 100 : null;
            } else {
              const activeCount = cohort?.members?.filter(m => m?.member_status === 'active')?.length;
              const retentionRate = month === 1 ? 100 : Math.max(Math.round((activeCount / cohort?.members?.length) * 100 * (1 - (month - 1) * 0.08)), 20);
              point[key] = retentionRate;
            }
          });
          chartData?.push(point);
        }

        setCohortData(chartData);
        setCohortLabels(labels);
        setActiveCohorts(Object.keys(cohorts));
      } catch (err) {
        console.error('Error fetching cohort data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCohortData();
  }, []);

  const toggleCohort = (cohort) => {
    setActiveCohorts(prev =>
      prev?.includes(cohort) ? prev?.filter(c => c !== cohort) : [...prev, cohort]
    );
  };

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-4 lg:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="text-base lg:text-lg font-semibold font-heading text-[var(--color-foreground)]">
            Cohort Retention Analysis
          </h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-caption mt-0.5">
            Member retention rate by monthly cohort
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(cohortLabels)?.map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleCohort(key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-caption font-medium border transition-all ${
                activeCohorts?.includes(key)
                  ? 'border-transparent text-white' :'border-[var(--color-border)] text-[var(--color-muted-foreground)] bg-transparent'
              }`}
              style={activeCohorts?.includes(key) ? { backgroundColor: cohortColors?.[key] } : {}}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-56">
          <span className="text-sm text-[var(--color-muted-foreground)] font-caption">Loading cohort data...</span>
        </div>
      ) : cohortData?.length === 0 ? (
        <div className="flex items-center justify-center h-56">
          <span className="text-sm text-[var(--color-muted-foreground)] font-caption">No cohort data available yet.</span>
        </div>
      ) : (
        <div className="w-full h-56 lg:h-64" aria-label="Cohort Retention Line Chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={cohortData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'Inter' }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'Inter' }}
                tickFormatter={v => `${v}%`}
                domain={[0, 100]}
              />
              <Tooltip
                formatter={value => [`${value}%`, '']}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontFamily: 'Inter',
                }}
              />
              {Object.entries(cohortColors)?.map(([key, color]) =>
                activeCohorts?.includes(key) && cohortLabels?.[key] ? (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 3, fill: color }}
                    activeDot={{ r: 5 }}
                    name={cohortLabels?.[key]}
                    connectNulls
                  />
                ) : null
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CohortAnalysis;